import { useMemo } from "react";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import {
  LanguageMap,
  NullableLanguageMap,
  useLanguage,
} from "../i18n/useLanguage";
import { useDealerId } from "../stores/hooks/useDealerId";
import { useNullableDealerAccountInfo } from "../stores/hooks/useNullableDealerAccountInfo";
import { useSearchForm } from "../stores/hooks/useSearchForm";
import { ComboBoxOption } from "../ui/ComboBox/useComboBox";
import { useGetVehicleManufacturers } from "./useGetVehicleManufacturers";

type Option = ComboBoxOption<string, VehicleModel>;

const getLabel = (
  trustedLanguage: "en" | "fr",
  vehicleModel?: VehicleModel
): string =>
  [
    vehicleModel?.vehicleModelClassName[trustedLanguage],
    vehicleModel?.vehicleModelName[trustedLanguage],
  ]
    .filter(Boolean)
    .join(" ");

export const useGetVehicleModels = (): ModifiedSWRResponse<
  FetcherResult<VehicleModel[]>,
  Option[]
> & {
  nationalAccountId: string;
  selectedOptionLabel: string;
  vehicleManufacturerId?: number;
  vehicleModelId?: string;
  vehicleYearId?: number;
} => {
  const { nationalAccountId, vehicleManufacturerId, vehicleYearId } =
    useGetVehicleManufacturers();
  const [language] = useLanguage();
  const [{ vehicleModelClassId, vehicleModelName }] = useSearchForm();
  const dealer = useNullableDealerAccountInfo();
  const dealerId = useDealerId();

  const { data, ...res } = useSwrGet<VehicleModel[]>(
    vehicleManufacturerId
      ? "/retailSiteWebService/vehicle/getVehicleModels.php"
      : null,
    {
      dealerId,
      nationalAccountId,
      vehicleManufacturerId: String(vehicleManufacturerId || ""),
      vehicleYearId: String(vehicleYearId || ""),
    }
  );

  const dealerIgnoreVehicleSortOrder = Number(
    dealer?.dealerIgnoreVehicleSortOrder
  );

  const options = useMemo(
    () =>
      data
        ?.map((vehicleModel) => {
          return {
            data: vehicleModel,
            label: getLabel(language, vehicleModel),
            value: vehicleModel.vehicleModelId,
          };
        })
        .sort((a, b) => {
          const labelCompared = a.label.localeCompare(b.label);
          if (dealerIgnoreVehicleSortOrder === 1) {
            return labelCompared;
          }
          return (
            Number(a.data.vehicleModelSortOrder) -
              Number(b.data.vehicleModelSortOrder) || labelCompared
          );
        }),
    [data, language, dealerIgnoreVehicleSortOrder]
  );

  if (!options) {
    return {
      ...res,
      data: undefined,
      nationalAccountId,
      selectedOptionLabel: "",
    };
  }

  const currentVehicleModel = options.find(({ data, label }) => {
    if (vehicleModelClassId) {
      return (
        data.vehicleModelClassId === vehicleModelClassId &&
        data.vehicleModelName[language]?.toLowerCase() ===
          vehicleModelName?.toLowerCase()
      );
    }
    return label?.toLowerCase() === vehicleModelName?.toLowerCase();
  });
  return {
    ...res,
    data: options,
    nationalAccountId,
    selectedOptionLabel: getLabel(language, currentVehicleModel?.data),
    vehicleManufacturerId,
    vehicleModelId: currentVehicleModel?.value,
    vehicleYearId,
  };
};

export type VehicleModel = {
  vehicleModelClassId: string;
  vehicleModelClassName: NullableLanguageMap;
  vehicleModelId: string;
  vehicleModelImage: string;
  vehicleModelName: LanguageMap;
  vehicleModelSortOrder: string;
};
